import React, { useEffect, useMemo, useState } from "react";
import { Typography, Box, Paper, Alert, Grid, Tabs, Tab } from "@mui/material";
import { useJobTitlesQuery } from "../../../../../services/profileService/profile-api";
import { useJobTitleMappingsQuery } from "../../../../../services/ticketEasyService/ticketEasy-api";
import { Loading } from "../../../../shared";
import { NotificationSnackbar } from "../../../components";
import { JobTitleMapSaveButton } from "./JobTitleMapSaveButton";
import { useAppDispatch } from "../../../../../app/hooks";
import "./../../../TicketEasy.css";
import { usePermissions } from "../../../hooks/usePermissions";
import { PSNTitleAutocomplete } from "./PSNTitleAutocomplete";
import { HSTRolesAutocomplete } from "./HSTRolesAutocomplete";
import { ExistingMappingsView } from "./ExistingMappingsView";

function JobTitleMapping() {
  const {
    isAdmin,
    isLoading: isPermissionsLoading,
    error: permissionsError,
  } = usePermissions();
  const {
    data: jobTitles,
    isLoading: isJobTitlesLoading,
    error: jobTitlesError,
  } = useJobTitlesQuery();
  const {
    data: jobTitleMappings,
    isLoading: isMappingsLoading,
    error: mappingsError,
    refetch: refetchMappings,
  } = useJobTitleMappingsQuery();

  const dispatch = useAppDispatch();

  const [assignments, setAssignments] = useState<{
    [key: string]: { relatedTitle: string; id: string };
  }>({});
  const [selectedPSNTitle, setSelectedPSNTitle] = useState<string>("");
  const [tabValue, setTabValue] = useState<number>(0);
  const [saveSuccess, setSaveSuccess] = useState<boolean>(false);
  const [saveError, setSaveError] = useState<string | null>(null);

  // Handle tab changes
  const handleTabChange = (_event: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
  };

  useEffect(() => {
    if (jobTitleMappings) {
      const initialAssignments = jobTitleMappings.reduce((acc, mapping) => {
        acc[mapping.jobTitle] = {
          relatedTitle: mapping.relatedTitle || "",
          id: mapping.id,
        };
        return acc;
      }, {} as { [key: string]: { relatedTitle: string; id: string } });
      setAssignments(initialAssignments);
    }
  }, [jobTitleMappings]);

  const handleRoleChange = (jobTitle: string, role: string) => {
    setAssignments((prev) => ({
      ...prev,
      [jobTitle]: {
        relatedTitle: role,
        id: prev[jobTitle]?.id || "0",
      },
    }));
  };

  const handlePSNTitleChange = (title: string) => {
    setSelectedPSNTitle(title);
  };

  // Transform assignments to JobTitleMap array for the ExistingMappingsView
  const existingMappings = useMemo(() => {
    return Object.entries(assignments).map(
      ([jobTitle, { relatedTitle, id }]) => ({
        jobTitle,
        relatedTitle,
        jobType: "HST",
        id,
      })
    );
  }, [assignments]); // Handle save success with refetch
  const handleSaveSuccess = () => {
    setSaveSuccess(true);
    // Refetch mappings to ensure data is up to date
    refetchMappings();
  };

  // Handle save error with detailed error message
  const handleSaveError = (error: any) => {
    let errorMessage = "Unknown error occurred while saving job title mapping";

    if (error) {
      if (typeof error === "string") {
        errorMessage = error;
      } else if (error.message) {
        errorMessage = error.message;
      } else if (error.data?.message) {
        errorMessage = error.data.message;
      } else if (error.status) {
        errorMessage = `Server error (${error.status})`;
      }
    }

    setSaveError(`Error saving job title mapping: ${errorMessage}`);
  };

  if (isPermissionsLoading || isJobTitlesLoading || isMappingsLoading) {
    return <Loading />;
  }

  if (permissionsError) {
    return (
      <Typography color="error">
        Error loading permissions. Please try again later.
      </Typography>
    );
  }

  if (!isAdmin) {
    return (
      <Box margin={3}>
        <Alert severity="warning">
          You do not have permission to access the Job Title Mapping section.
        </Alert>
      </Box>
    );
  }

  if (jobTitlesError || mappingsError) {
    return (
      <Typography color="error">
        Error loading data. Please try again later.
      </Typography>
    );
  }

  return (
    <Box
      sx={{
        height: "100%",
        width: "100%",
        display: "flex",
        flexDirection: "column",
        padding: 3,
      }}
    >
      <Typography variant="h4" component="h1" gutterBottom>
        Job Title Mapping
      </Typography>
      <Tabs
        value={tabValue}
        onChange={handleTabChange}
        aria-label="job title mapping tabs"
      >
        <Tab label="Create Mapping" />
        <Tab label="View Mappings" />
      </Tabs>
      {tabValue === 0 && (
        <Paper
          elevation={2}
          sx={{
            p: 3,
            mt: 2,
            flex: 1,
            display: "flex",
            flexDirection: "column",
            overflow: "hidden",
          }}
        >
          <Typography variant="h6" gutterBottom>
            Create New Mapping
          </Typography>

          <Grid container spacing={3} sx={{ mt: 1 }}>
            <Grid item xs={12} md={5}>
              <Typography variant="subtitle1" gutterBottom>
                Select PSN Job Title
              </Typography>
              <PSNTitleAutocomplete
                jobTitles={jobTitles || []}
                selectedTitle={selectedPSNTitle}
                onChange={handlePSNTitleChange}
              />
            </Grid>

            <Grid item xs={12} md={5}>
              <Typography variant="subtitle1" gutterBottom>
                Select HST Role
              </Typography>
              <HSTRolesAutocomplete
                jobTitle={selectedPSNTitle}
                selectedRole={assignments[selectedPSNTitle]?.relatedTitle || ""}
                onChange={handleRoleChange}
              />
            </Grid>

            <Grid
              item
              xs={12}
              md={2}
              sx={{
                display: "flex",
                alignItems: "flex-end",
                justifyContent: "flex-end",
              }}
            >
              <JobTitleMapSaveButton
                assignments={assignments}
                onSaveSuccess={handleSaveSuccess}
                onSaveError={handleSaveError}
              />
            </Grid>
          </Grid>

          {/* Show the existing mappings in the create view as well for context */}
          <Box
            sx={{
              flex: 1,
              overflow: "hidden",
              display: "flex",
              flexDirection: "column",
            }}
          >
            <ExistingMappingsView 
              mappings={existingMappings} 
              isViewMode={true}
            />
          </Box>
        </Paper>
      )}
      {tabValue === 1 && (
        <Paper
          elevation={2}
          sx={{
            p: 3,
            mt: 2,
            flex: 1,
            display: "flex",
            flexDirection: "column",
            overflow: "hidden",
          }}
        >
          <Typography variant="h6" gutterBottom>
            Job Title Mappings
          </Typography>
          <Box
            sx={{
              flex: 1,
              overflow: "hidden",
              display: "flex",
              flexDirection: "column",
            }}
          >
            <ExistingMappingsView 
              mappings={existingMappings} 
              isViewMode={false}
              onDeleteSuccess={refetchMappings}
            />
          </Box>
        </Paper>
      )}{" "}
      {/* Notification System */}
      <NotificationSnackbar
        open={saveSuccess}
        message="Job title mapping has been saved successfully"
        severity="success"
        onClose={() => setSaveSuccess(false)}
        autoHideDuration={4000}
      />
      <NotificationSnackbar
        open={!!saveError}
        message={saveError || ""}
        severity="error"
        onClose={() => setSaveError(null)}
        autoHideDuration={6000}
      />
    </Box>
  );
}

export { JobTitleMapping };
